"use client"
import { useMemo, useRef } from "react"
import {
  type PlanId,
  type ResolvedBranchesData,
  getSelectedPlanFromPath,
} from "@/lib/branchTypes"
import { usePlaybookScrollState } from "./usePlaybookScrollState"
import { usePlaybookSelectionState } from "./usePlaybookSelectionState"

export const usePlaybookState = (
  branchData: ResolvedBranchesData,
  isEpilogueExpanded: boolean
) => {
  const contentSectionRef = useRef<HTMLDivElement | null>(null)
  const selectionState = usePlaybookSelectionState(branchData)
  const selectedPlan: PlanId | null = useMemo(
    () => getSelectedPlanFromPath(selectionState.selectedPath),
    [selectionState.selectedPath]
  )
  const scrollState = usePlaybookScrollState(
    contentSectionRef,
    isEpilogueExpanded,
    // The galaxy end sequence (globe handing off to the starfield at the
    // Epilogue header) belongs to Plan A's epilogue alone — branch plans
    // whose dates run late (S reaches 2045+) keep their frozen globe on
    // screen.
    selectedPlan === "A",
    // Sub-year date interpolation is only for the branch plans with a
    // 0.1-year phase grid (B/C/D). Plan S carries annual phases (2030→2045),
    // so it stays on stepwise markers instead of interpolating across
    // year-wide gaps.
    selectedPlan !== null && selectedPlan !== "A" && selectedPlan !== "S",
    // Plan A (and the unchosen default view) interpolates only through the
    // 2029-2030 deal window — see interpolatePhaseGridInDealWindow.
    selectedPlan === null || selectedPlan === "A"
  )
  const activePlan: PlanId = selectionState.hoveredPlan ?? selectedPlan ?? "D"
  // While the reader is scrolled above the plan choice point, the HUD presents
  // the pre-choice view (a null selectedPlan pins the graph to the branch
  // point). Only these hud* values revert — the real selection stays intact,
  // so the expanded content, URL, and scroll lock are untouched and scrolling
  // back down restores the chosen plan's view.
  const hudSelectedPlan: PlanId | null =
    scrollState.isAboveChoicePoint ? null : selectedPlan
  const hudActivePlan: PlanId = selectionState.hoveredPlan ?? hudSelectedPlan ?? "D"
  // While a non-A plan is selected, the reader is off Plan A's timeline — the
  // HUD date advances through the branch's own markers — so the metric
  // explainer's now-dot and readout ride that plan's branch line (its full
  // spreadsheet-extracted series) instead of Plan A's.
  const metricComparisonPlan = hudSelectedPlan !== null && hudSelectedPlan !== "A" ? hudSelectedPlan : null

  return {
    metricComparisonPlan,
    activePlan,
    contentSectionRef,
    hudActivePlan,
    hudSelectedPlan,
    selectedPlan,
    ...selectionState,
    ...scrollState,
  }
}
